import { Currency } from "@redotech/money/currencies";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { PaymentProviderType } from "../billing/billing-engine-types";
import {
  BillingPeriod,
  CheckoutOptimizationBillingPeriod,
} from "../billing/service-invoice";
import { zUsdCents } from "../currency";
import { InsertionPosition } from "../widget";
import { FulfillmentCutoffScheduleParser } from "./fulfillment-cutoff";

const baseConversionBillingSchema = z.object({
  enabled: z.boolean(),
  startDate: z.date().optional(),
  renewOnPeriodEnd: z.boolean(),
  paymentProvider: createEnum(PaymentProviderType, {
    enumName: "PaymentProviderType",
  }).optional(),
});

const conversionUsageBillingSchema = baseConversionBillingSchema.extend({
  /**
   * For backwards compatibility, we allow the type to be undefined as
   * this field didn't exist in the past and all existing records have a type of "usage".
   */
  billingType: z.literal("usage"),
  period: z.literal(CheckoutOptimizationBillingPeriod.BI_WEEKLY),
  pricePerOrderInCents: z.number(),
});
export type CheckoutOptimizationUsageBilling = z.infer<
  typeof conversionUsageBillingSchema
>;

const conversionSubscriptionBillingSchema = baseConversionBillingSchema.extend({
  billingType: z.literal("subscription"),
  period: z.literal(BillingPeriod.MONTHLY),
  periodFeeInCents: zUsdCents,
  pricePerOrderInCents: zUsdCents.optional(),
});
export type CheckoutOptimizationSubscriptionBilling = z.infer<
  typeof conversionSubscriptionBillingSchema
>;

export const checkoutOptimizationBillingSchema = z.union([
  conversionUsageBillingSchema,
  conversionSubscriptionBillingSchema,
]);
export type CheckoutOptimizationBilling = z.infer<
  typeof checkoutOptimizationBillingSchema
>;

const postPurchaseUpsellSchema = z.object({
  createOrderOnUpsell: z.boolean().optional(),
});

export const progressBarSchema = z.object({
  threshold: z.object({
    amount: z.number(),
    currency: createEnum(Currency, { enumName: "Currency" }),
  }),
  ltThresholdText: z.string(),
  gteThresholdText: z.string(),
  progressBarColor: z.string(),
  selectors: z.array(
    z.object({
      selector: z.string(),
      insertPosition: createEnum(InsertionPosition, {
        enumName: "InsertionPosition",
      }),
    }),
  ),
});
export type ProgressBar = z.infer<typeof progressBarSchema>;

const checkoutOptimizationSettingsSchema = z.object({
  enabled: z.boolean(),
  infinityTestingEnabled: z.boolean().optional(),
  preventSplitShipmentsRateAddition: z.boolean().optional(),
  postPurchaseUpsell: postPurchaseUpsellSchema.optional(),
  fulfillmentCutoffSchedule: FulfillmentCutoffScheduleParser.optional(),
  checkoutOptimizationBilling: checkoutOptimizationBillingSchema.optional(),
  ensureDiscountsCombineWithShippingDiscounts: z.boolean().optional(),
  subscriptionsModeEnabled: z.boolean().optional(),
  storefrontProgressBar: progressBarSchema.optional(),
  useMultiParcel: z.boolean().optional(),
  useShippingOptionMetafields: z.boolean().optional(),
});

export type CheckoutOptimizationSettings = z.infer<
  typeof checkoutOptimizationSettingsSchema
>;

export const checkoutOptimizationSettingsDefault: CheckoutOptimizationSettings =
  {
    enabled: false,
    infinityTestingEnabled: false,
    preventSplitShipmentsRateAddition: false,
    ensureDiscountsCombineWithShippingDiscounts: false,
  };

const CheckoutOptimizationAnalyticsAssumptionsZodSchema = z.object({
  annualOrders: z.number().optional(),
});
export type CheckoutOptimizationAnalyticsAssumptions = z.infer<
  typeof CheckoutOptimizationAnalyticsAssumptionsZodSchema
>;

export type CheckoutOptimizationBillingCreator = {
  /**
   * Distinguishes this billing creator type from any other string to
   * ensure the creator conforms to the correct string format.
   */
  __checkoutOptimizationBillingCreator: true;
  value: string;
};

export function getBillingSettingsCreator({
  creatorEmail,
}: {
  creatorEmail: string;
}): CheckoutOptimizationBillingCreator {
  return {
    __checkoutOptimizationBillingCreator: true,
    value: `admin:${creatorEmail}`,
  };
}
